import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, Package } from "lucide-react";
import { adminClient, notifyProductsChanged } from "./api";
import ImageUploader from "./ImageUploader";

const EMPTY = {
  title: "", category: "Furniture", price: "", retail: "",
  condition: "Grade A Refurbished", co2_saved: "0 kg",
  image: "", description: "", is_active: true, sort_order: 0,
  delivery_fee: 25, pickup_available: true,
};

const CATEGORIES = ["Furniture", "Power Tools", "Electronics", "As Is/Salvaged Goods", "Other"];
const CONDITIONS = ["Grade A Refurbished", "Vintage Patina", "Tested & Certified Working", "As-Is Salvage"];

export default function MarketplaceAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminClient.get("/admin/marketplace");
      setItems(data.items || []);
    } catch { toast.error("Could not load items"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.image) return toast.error("Please upload an image");
    const payload = {
      title: form.title, category: form.category,
      price: parseFloat(form.price) || 0,
      retail: parseFloat(form.retail) || 0,
      condition: form.condition, co2_saved: form.co2_saved || "0 kg",
      image: form.image, description: form.description || "",
      is_active: !!form.is_active, sort_order: parseInt(form.sort_order, 10) || 0,
      delivery_fee: parseFloat(form.delivery_fee) || 0,
      pickup_available: !!form.pickup_available,
    };
    try {
      if (editing) {
        await adminClient.put(`/admin/marketplace/${editing.id}`, payload);
        toast.success("Updated · Live on site now", { icon: "🟢" });
      } else {
        await adminClient.post("/admin/marketplace", payload);
        toast.success("Item added · Live on site now", { icon: "🟢" });
      }
      notifyProductsChanged();
      setOpen(false);
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await adminClient.delete(`/admin/marketplace/${item.id}`);
      toast.success("Deleted · Removed from site", { icon: "🟢" });
      notifyProductsChanged();
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggleActive = async (item) => {
    try {
      await adminClient.put(`/admin/marketplace/${item.id}`, { ...item, is_active: !item.is_active });
      toast.success(item.is_active ? "Hidden from site" : "Now live on site", { icon: "🟢" });
      notifyProductsChanged();
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="marketplace-admin-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-black text-2xl text-[#0C2E2B]">Marketplace</h1>
          <p className="text-sm text-zinc-500">Slightly used goods you've reclaimed.</p>
        </div>
        <Button data-testid="marketplace-admin-add-btn" onClick={openCreate} className="btn-kinetic rounded-md text-xs">
          <Plus className="w-4 h-4 mr-1" /> New Item
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading...</div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center">
          <Package className="w-10 h-10 mx-auto text-zinc-400 mb-3" />
          <p className="font-bold text-zinc-800">No items yet.</p>
          <Button onClick={openCreate} className="mt-4 btn-kinetic rounded-md text-xs">Add your first item</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((it) => (
            <Card key={it.id} data-testid={`marketplace-admin-card-${it.id}`} className="overflow-hidden border-2 border-zinc-200">
              <div className="relative">
                <img src={it.image} alt={it.title} className="w-full h-40 object-cover" />
                {!it.is_active && <div className="absolute inset-0 bg-black/60 grid place-items-center text-white font-bold text-sm">HIDDEN</div>}
              </div>
              <div className="p-4">
                <div className="text-[10px] font-tactical uppercase tracking-widest text-zinc-500">{it.category}</div>
                <div className="flex justify-between items-start gap-2 mt-1">
                  <h3 className="font-display font-black text-[#0C2E2B]">{it.title}</h3>
                  <div className="text-right">
                    <div className="font-mono-data font-black text-[#0C2E2B]">${it.price}</div>
                    <div className="text-[10px] text-zinc-400 line-through">${it.retail}</div>
                  </div>
                </div>
                <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{it.description}</p>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-xs">
                    <Switch data-testid={`marketplace-admin-toggle-${it.id}`} checked={it.is_active} onCheckedChange={() => toggleActive(it)} />
                    <span className="text-zinc-600">{it.is_active ? "Live" : "Hidden"}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button data-testid={`marketplace-admin-edit-${it.id}`} size="sm" variant="outline" className="rounded-md h-8" onClick={() => openEdit(it)}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`marketplace-admin-delete-${it.id}`} size="sm" variant="outline" className="rounded-md h-8 text-red-600 hover:text-red-700" onClick={() => remove(it)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit item" : "New item"}</DialogTitle>
            <DialogDescription>Changes go live on the Used Goods page as soon as you save.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="marketplace-admin-form">
            <ImageUploader value={form.image} onChange={(u) => setForm({ ...form, image: u })} testId="mkt-image-uploader" />
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Title</Label>
              <Input data-testid="mkt-form-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Category</Label>
                <select data-testid="mkt-form-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm bg-white">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Condition</Label>
                <select data-testid="mkt-form-condition-select" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm bg-white">
                  {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Price ($)</Label>
                <Input data-testid="mkt-form-price-input" required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Retail ($)</Label>
                <Input data-testid="mkt-form-retail-input" type="number" step="0.01" value={form.retail} onChange={(e) => setForm({ ...form, retail: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">CO₂ saved</Label>
                <Input data-testid="mkt-form-co2-input" value={form.co2_saved} onChange={(e) => setForm({ ...form, co2_saved: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Description</Label>
              <Textarea data-testid="mkt-form-description-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3 items-center">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Delivery fee ($)</Label>
                <Input data-testid="mkt-form-delivery-fee-input" type="number" step="0.01" value={form.delivery_fee} onChange={(e) => setForm({ ...form, delivery_fee: e.target.value })} className="mt-1" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch data-testid="mkt-form-pickup-switch" checked={form.pickup_available} onCheckedChange={(v) => setForm({ ...form, pickup_available: v })} />
                <span className="text-sm">Pickup available (free)</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-center">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Sort order</Label>
                <Input data-testid="mkt-form-sort-input" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch data-testid="mkt-form-active-switch" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <span className="text-sm">{form.is_active ? "Live on site" : "Hidden"}</span>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="mkt-form-submit-btn" type="submit" className="btn-kinetic rounded-md flex-1">
                {editing ? "Save changes" : "Add item"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
